import { useState, useCallback, useRef, useEffect } from "react";
import debounce from "lodash.debounce";
import { fetchData } from "../lib/dataCache";
import { stateNameForCode } from "../lib/search/stateAbbreviations";

const MIN_CHARS = 3;
const EXACT_CODE_CHARS = 2;
const DEBOUNCE_MS = 200;

const toPlaceMatch = (item) => ({
  id: `place:${item.name}`,
  // Never split on the comma -- "Memphis, Tennessee" is one token, not two.
  display: item.name,
  category: "Place",
  sourceType: "place",
  payload: item,
});

// Two escalation timers: "Searching..." until STILL_SEARCHING_MS, "Still searching..." until
// TIMEOUT_MS, then "Couldn't load matches" + a manual retry.
const STILL_SEARCHING_MS = 2500;
const TIMEOUT_MS = 10000;

export function usePlaceMatches(fragment) {
  const [placeMatches, setPlaceMatches] = useState([]);
  const [status, setStatus] = useState("idle");

  // Which keystroke (or retry) a response belongs to -- see FuzzyGeoSearch.js for why this
  // exists (a slow response landing after the user has typed past it must not repopulate stale
  // suggestions). Also what a stray timer callback checks before touching state, so a request
  // that already settled (or was superseded) can't flip the status back after the fact.
  const latestRequest = useRef(0);
  const stillSearchingTimer = useRef(null);
  const timeoutTimer = useRef(null);
  const lastValueRef = useRef("");

  const clearTimers = () => {
    clearTimeout(stillSearchingTimer.current);
    clearTimeout(timeoutTimer.current);
  };

  // The actual lookup, undebounced -- typing goes through the debounced wrapper below, but
  // "Try again" needs to fire immediately, not wait out another debounce window.
  const runLookup = useCallback(async (value) => {
    if (value.length < MIN_CHARS) {
      latestRequest.current += 1;
      clearTimers();
      setStatus("idle");
      // Two characters only ever match a known exact code (NY -> New York), resolved locally.
      const stateName = value.length === EXACT_CODE_CHARS ? stateNameForCode(value) : null;
      setPlaceMatches(stateName ? [toPlaceMatch({ name: stateName })] : []);
      return;
    }

    const requestId = ++latestRequest.current;
    clearTimers();
    setStatus("searching");
    stillSearchingTimer.current = setTimeout(() => {
      if (requestId === latestRequest.current) setStatus("stillSearching");
    }, STILL_SEARCHING_MS);
    timeoutTimer.current = setTimeout(() => {
      if (requestId === latestRequest.current) setStatus("error");
    }, TIMEOUT_MS);

    try {
      const items = await fetchData(`/api/geodb?query=${encodeURIComponent(value)}`);
      if (requestId !== latestRequest.current) return;
      clearTimers();
      setPlaceMatches(items.map(toPlaceMatch));
      setStatus("idle");
    } catch (error) {
      if (requestId !== latestRequest.current) return;
      clearTimers();
      // A 404 here means the backend genuinely has no place matching this query (confirmed
      // real-world case: "United States" isn't in geo_index at all) -- a normal, expected
      // answer, not a failure. Only anything else (network down, 5xx, timeout) is the real
      // "Couldn't load matches" / Try again case this status exists for.
      if (error.response?.status === 404) {
        setPlaceMatches([]);
        setStatus("idle");
        return;
      }
      console.error("Error fetching place matches:", error);
      setStatus("error");
      // Deliberately not clearing placeMatches -- the previous list stays up, dimmed by
      // CommandPalette, rather than blanking the palette out from under the user.
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- lodash.debounce wrapper; stable enough for search UX
  const debouncedLookup = useCallback(debounce(runLookup, DEBOUNCE_MS), [runLookup]);

  useEffect(() => {
    lastValueRef.current = fragment || "";
    debouncedLookup(fragment || "");
  }, [fragment, debouncedLookup]);

  // Clears any pending timers AND the pending debounce itself on unmount -- without
  // `debouncedLookup.cancel()`, a keystroke right before unmount (e.g. the bar closing) left its
  // debounce timer armed; it fired anyway, well after the component was gone, calling
  // setState on an unmounted hook.
  useEffect(() => () => {
    clearTimers();
    debouncedLookup.cancel();
  }, [debouncedLookup]);

  const retry = useCallback(() => runLookup(lastValueRef.current), [runLookup]);

  // Submitting makes any pending or in-flight lookup moot.
  const cancelPending = useCallback(() => {
    debouncedLookup.cancel();
    latestRequest.current += 1;
    clearTimers();
    setStatus("idle");
  }, [debouncedLookup]);

  return {
    placeMatches,
    isLoadingPlaces: status === "searching" || status === "stillSearching",
    status,
    retry,
    cancelPending,
  };
}
